package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.BorderStroke
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DirectionsBus
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.TouchApp

// ═══ MAPA Y CONFIGURADOR VISUAL DE ASIENTOS ═══
// Extraído de AdminScreen.kt. MapaAsientosDinamico también lo usa GestionBoletosScreen.

@Composable
fun MapaAsientosDinamico(
    layout: VehiculoConfig,
    asientosInfo: List<AdminSeatInfo>,
    onAsientoClick: (AdminSeatInfo) -> Unit
) {
    val infoMap = asientosInfo.associateBy { it.asiento }
    Card(
        modifier = Modifier.widthIn(max = 320.dp),
        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoPanel),
        shape = RoundedCornerShape(topStart = 40.dp, topEnd = 40.dp, bottomStart = 16.dp, bottomEnd = 16.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
        border = BorderStroke(2.dp, SmartVanColors.Borde)
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(12.dp), horizontalAlignment = Alignment.CenterHorizontally) {
          CarroceriaVehiculo(filas = layout.filas) {
            layout.filas.forEach { fila ->
                Row(
                    modifier = Modifier.width(280.dp),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    fila.asientos.forEach { celda ->
                        Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                            when {
                                celda == -1 -> {
                                    // Volante (chofer)
                                    Box(
                                        modifier = Modifier.size(58.dp).background(Color(0xFF455A64), RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp, bottomStart = 8.dp, bottomEnd = 8.dp)),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                            Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                                            Text("VOL", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                        }
                                    }
                                }
                                celda == 0 -> {
                                    // Pasillo/vacío
                                    Spacer(modifier = Modifier.size(58.dp))
                                }
                                else -> {
                                    // Asiento con número
                                    val seatInfo = infoMap[celda] ?: AdminSeatInfo(asiento = celda, status = AdminSeatStatus.LIBRE)
                                    AdminSeatItem(seatInfo) { onAsientoClick(seatInfo) }
                                }
                            }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(10.dp))
            }
          }
        }
    }
}

// ═══ CONFIGURADOR VISUAL DE ASIENTOS (v2) ═══

// Los asientos de las unidades reales YA vienen numerados de fábrica, y ese número es el que
// ve el pasajero al subir. Por eso la numeración es MANUAL: el admin la escribe y el sistema
// nunca la reescribe sola. Sólo se autoasigna a un asiento recién creado (marcador 99), y
// tomando el número libre más bajo para no pisar los existentes.
private const val ASIENTO_NUEVO = 99

private fun numerosUsados(filas: List<FilaVehiculo>): Set<Int> =
    filas.flatMap { f -> f.asientos.filter { it > 0 && it != ASIENTO_NUEVO } }.toSet()

/** Asigna número sólo a las celdas recién creadas; respeta todo lo que ya tenía número. */
private fun asignarNumerosNuevos(filas: List<FilaVehiculo>): List<FilaVehiculo> {
    val usados = numerosUsados(filas).toMutableSet()
    fun siguienteLibre(): Int { var n = 1; while (n in usados) n++; usados.add(n); return n }
    return filas.map { f ->
        FilaVehiculo(f.asientos.map { a -> if (a == ASIENTO_NUEVO) siguienteLibre() else a })
    }
}

/** Problemas que hacen inválido el layout. Vacío = numeración correcta. */
internal fun problemasNumeracion(filas: List<FilaVehiculo>): List<String> {
    val nums = filas.flatMap { f -> f.asientos.filter { it > 0 } }
    val problemas = mutableListOf<String>()
    val repetidos = nums.groupingBy { it }.eachCount().filter { it.value > 1 }.keys.sorted()
    if (repetidos.isNotEmpty()) problemas.add("Números repetidos: ${repetidos.joinToString(", ")}")
    // Un hueco no rompe nada por sí solo, pero casi siempre es un descuido al capturar.
    if (nums.isNotEmpty()) {
        val faltantes = (1..nums.max()).filter { it !in nums }
        if (faltantes.isNotEmpty()) problemas.add("Sin usar: ${faltantes.joinToString(", ")}")
    }
    return problemas
}

// Plantillas predefinidas
private fun plantillaVehiculo(tipo: String): List<FilaVehiculo> = when (tipo) {
    "sprinter_13" -> listOf(
        FilaVehiculo(listOf(-1, 0, 0, 1)),
        FilaVehiculo(listOf(2, 3, 0, 4)),
        FilaVehiculo(listOf(5, 6, 0, 7)),
        FilaVehiculo(listOf(8, 9, 0, 10)),
        FilaVehiculo(listOf(11, 12, 13, 0)),
    )
    "van_8" -> listOf(
        FilaVehiculo(listOf(-1, 0, 0, 1)),
        FilaVehiculo(listOf(2, 3, 0, 4)),
        FilaVehiculo(listOf(5, 6, 7, 8)),
    )
    "sprinter_15" -> listOf(
        FilaVehiculo(listOf(-1, 0, 0, 1)),
        FilaVehiculo(listOf(2, 3, 0, 4)),
        FilaVehiculo(listOf(5, 6, 0, 7)),
        FilaVehiculo(listOf(8, 9, 0, 10)),
        FilaVehiculo(listOf(11, 12, 0, 13)),
        FilaVehiculo(listOf(14, 15, 0, 0)),
    )
    "sprinter_19" -> listOf(
        FilaVehiculo(listOf(-1, 0, 0, 1)),
        FilaVehiculo(listOf(2, 3, 0, 4)),
        FilaVehiculo(listOf(5, 6, 0, 7)),
        FilaVehiculo(listOf(8, 9, 0, 10)),
        FilaVehiculo(listOf(11, 12, 0, 13)),
        FilaVehiculo(listOf(14, 15, 0, 16)),
        FilaVehiculo(listOf(17, 18, 19, 0)),
    )
    "van_10_3col" -> listOf(
        FilaVehiculo(listOf(-1, 0, 1)),
        FilaVehiculo(listOf(2, 0, 3)),
        FilaVehiculo(listOf(4, 0, 5)),
        FilaVehiculo(listOf(6, 0, 7)),
        FilaVehiculo(listOf(8, 9, 10)),
    )
    else -> plantillaVehiculo("sprinter_13")
}

/**
 * Quita un asiento y compacta la numeración.
 *
 * Los números representan el ORDEN en que se fueron asignando, así que al borrar el 5 los
 * siguientes recorren una posición. Sin esto quedaban huecos y el admin tenía que rehacer
 * el layout completo para corregir un solo asiento.
 */
private fun quitarAsiento(filas: List<FilaVehiculo>, numero: Int): List<FilaVehiculo> =
    filas.map { f ->
        FilaVehiculo(f.asientos.map { a ->
            when {
                a == numero -> 0
                a > numero -> a - 1
                else -> a
            }
        })
    }

@Composable
fun ConfiguradorAsientos(
    filasActuales: List<FilaVehiculo>,
    columnas: Int,
    onFilasChange: (List<FilaVehiculo>) -> Unit
) {
    var columnasActuales by remember { mutableStateOf(columnas) }
    var filaSeleccionada by remember { mutableStateOf(-1) }
    // Qué se pinta al tocar una celda. Asiento es lo normal; los otros dos son para el pasillo
    // y la posición del chofer.
    var modo by remember { mutableStateOf("asiento") }
    // Asientos "elegidos" en la vista previa. No se guardan: existen solo para que el admin
    // compruebe el tacto real de la pantalla del cliente antes de publicar la unidad.
    var seleccionPreview by remember { mutableStateOf(setOf<Int>()) }

    val totalAsientos = filasActuales.sumOf { fila -> fila.asientos.count { it > 0 } }
    val siguienteNumero = totalAsientos + 1

    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
      val ladoALado = maxWidth >= 620.dp

      val editor = @Composable {
        Column(modifier = Modifier.fillMaxWidth()) {
            Text("Diseña el layout del vehículo", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
            Spacer(modifier = Modifier.height(8.dp))

            // ═══ PLANTILLAS RÁPIDAS ═══
            Text("Plantillas rápidas", fontWeight = FontWeight.Medium, color = Color.Gray, style = MaterialTheme.typography.labelMedium)
            Spacer(modifier = Modifier.height(4.dp))
            CarruselHorizontal(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                val plantillas = listOf(
                    "Van 8" to "van_8",
                    "Sprinter 13" to "sprinter_13",
                    "Sprinter 15" to "sprinter_15",
                    "Sprinter 19" to "sprinter_19",
                    "Van 10 (3 col)" to "van_10_3col",
                )
                items(plantillas.size) { idx ->
                    val (nombre, clave) = plantillas[idx]
                    val pl = plantillaVehiculo(clave)
                    val cap = pl.sumOf { f -> f.asientos.count { it > 0 } }
                    OutlinedButton(
                        onClick = { columnasActuales = pl.first().asientos.size; onFilasChange(pl) },
                        shape = RoundedCornerShape(20.dp),
                        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                        border = BorderStroke(1.dp, SmartVanColors.CyanAccent.copy(alpha = 0.5f))
                    ) { Text("$nombre ($cap)", fontSize = 11.sp, color = SmartVanColors.AzulOscuro) }
                }
            }
            Spacer(modifier = Modifier.height(12.dp))

            // ═══ MODO DE PINTADO + SIGUIENTE NÚMERO ═══
            Text("Qué pintar al tocar", fontWeight = FontWeight.Medium, color = Color.Gray, style = MaterialTheme.typography.labelSmall)
            Spacer(modifier = Modifier.height(4.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                listOf("asiento" to "Asiento", "pasillo" to "Pasillo", "volante" to "Volante").forEach { (clave, etiqueta) ->
                    val sel = modo == clave
                    Button(
                        onClick = { modo = clave },
                        modifier = Modifier.weight(1f),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = when {
                                !sel -> SmartVanColors.Borde
                                clave == "asiento" -> SmartVanColors.Verde
                                clave == "volante" -> Color(0xFF455A64)
                                else -> SmartVanColors.AzulOscuro
                            }
                        ),
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 6.dp)
                    ) { Text(etiqueta, color = if (sel) Color.White else Color.DarkGray, fontWeight = FontWeight.Bold, fontSize = 11.sp) }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))

            if (modo == "asiento") {
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text("Siguiente asiento: $siguienteNumero", fontWeight = FontWeight.Black, color = SmartVanColors.VerdeOscuro)
                            Text("Toca los asientos en el orden que quieras numerarlos. Toca uno ya puesto para quitarlo.",
                                color = Color.Gray, fontSize = 10.sp)
                        }
                        if (totalAsientos > 0) {
                            TextButton(onClick = {
                                // Deja el layout (pasillos y volante) y borra sólo los números:
                                // así se puede renumerar de cero sin rehacer la forma.
                                onFilasChange(filasActuales.map { f ->
                                    FilaVehiculo(f.asientos.map { if (it > 0) 0 else it })
                                })
                            }) { Text("Borrar números", fontSize = 11.sp, color = Color(0xFFC62828)) }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            // ═══ COLUMNAS ═══
            Text("Columnas", fontWeight = FontWeight.Medium, color = Color.Gray, style = MaterialTheme.typography.labelSmall)
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                listOf(3, 4, 5).forEach { numCols ->
                    val selected = columnasActuales == numCols
                    Button(
                        onClick = {
                            if (columnasActuales != numCols) {
                                columnasActuales = numCols
                                onFilasChange(filasActuales.map { fila ->
                                    val a = fila.asientos.toMutableList()
                                    while (a.size < numCols) a.add(0)
                                    while (a.size > numCols) a.removeLast()
                                    FilaVehiculo(a)
                                })
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = if (selected) SmartVanColors.AzulOscuro else SmartVanColors.Borde),
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 6.dp)
                    ) { Text("$numCols", color = if (selected) Color.White else Color.DarkGray, fontWeight = FontWeight.Bold) }
                }
            }
            Spacer(modifier = Modifier.height(10.dp))

            // ═══ GRID DEL VEHÍCULO ═══
            CarroceriaVehiculo(filas = filasActuales, modifier = Modifier.fillMaxWidth()) {
                filasActuales.forEachIndexed { filaIdx, fila ->
                    val isSelected = filaSeleccionada == filaIdx
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                            .then(if (isSelected) Modifier.background(SmartVanColors.CyanAccent.copy(alpha = 0.12f), RoundedCornerShape(8.dp)).padding(2.dp) else Modifier.padding(2.dp))
                    ) {
                        Box(
                            modifier = Modifier.width(20.dp).height(44.dp)
                                .background(if (isSelected) SmartVanColors.CyanAccent else Color.Transparent, RoundedCornerShape(4.dp))
                                .clickable { filaSeleccionada = if (isSelected) -1 else filaIdx },
                            contentAlignment = Alignment.Center
                        ) {
                            Text("${filaIdx + 1}", fontSize = 9.sp, fontWeight = FontWeight.Bold, color = if (isSelected) Color.White else Color.LightGray)
                        }

                        fila.asientos.forEachIndexed { colIdx, celda ->
                            val bgColor = when {
                                celda == -1 -> Color(0xFF455A64)
                                celda == 0 -> SmartVanColors.FondoGris
                                else -> SmartVanColors.Verde
                            }
                            Box(
                                modifier = Modifier.weight(1f).height(44.dp)
                                    .background(bgColor, RoundedCornerShape(8.dp))
                                    .clickable {
                                        when (modo) {
                                            // El número sale del ORDEN de selección: el asiento
                                            // tocado toma el siguiente. Tocar uno ya numerado lo
                                            // quita y recorre los posteriores.
                                            "asiento" -> {
                                                if (celda > 0) {
                                                    onFilasChange(quitarAsiento(filasActuales, celda))
                                                } else {
                                                    val nuevas = filasActuales.toMutableList()
                                                    val f = nuevas[filaIdx].asientos.toMutableList()
                                                    f[colIdx] = siguienteNumero
                                                    nuevas[filaIdx] = FilaVehiculo(f)
                                                    onFilasChange(nuevas)
                                                }
                                            }
                                            "pasillo" -> {
                                                if (celda > 0) onFilasChange(quitarAsiento(filasActuales, celda))
                                                else {
                                                    val nuevas = filasActuales.toMutableList()
                                                    val f = nuevas[filaIdx].asientos.toMutableList()
                                                    f[colIdx] = 0
                                                    nuevas[filaIdx] = FilaVehiculo(f)
                                                    onFilasChange(nuevas)
                                                }
                                            }
                                            "volante" -> {
                                                // El volante es único en todo el layout.
                                                var base = if (celda > 0) quitarAsiento(filasActuales, celda) else filasActuales
                                                base = base.map { f -> FilaVehiculo(f.asientos.map { if (it == -1) 0 else it }) }
                                                val nuevas = base.toMutableList()
                                                val f = nuevas[filaIdx].asientos.toMutableList()
                                                f[colIdx] = -1
                                                nuevas[filaIdx] = FilaVehiculo(f)
                                                onFilasChange(nuevas)
                                            }
                                        }
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                when {
                                    celda == -1 -> Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = Color.White, modifier = Modifier.size(14.dp))
                                        Text("VOL", color = Color.White, fontWeight = FontWeight.Bold, fontSize = 8.sp)
                                    }
                                    celda == 0 -> Text("·", color = Color.Gray, fontSize = 16.sp)
                                    else -> Text("$celda", color = Color.White, fontWeight = FontWeight.Black, fontSize = 14.sp)
                                }
                            }
                        }

                        if (isSelected && filasActuales.size > 2) {
                            Box(
                                modifier = Modifier.size(24.dp)
                                    .background(Color.Red.copy(alpha = 0.1f), RoundedCornerShape(4.dp))
                                    .clickable {
                                        // Al borrar la fila se quitan sus asientos uno a uno para
                                        // que la numeración quede compacta.
                                        var base = filasActuales
                                        filasActuales[filaIdx].asientos.filter { it > 0 }.sortedDescending()
                                            .forEach { n -> base = quitarAsiento(base, n) }
                                        val nuevas = base.toMutableList()
                                        nuevas.removeAt(filaIdx)
                                        filaSeleccionada = -1
                                        onFilasChange(nuevas)
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(Icons.Filled.Delete, contentDescription = "Eliminar fila", tint = Color.Red, modifier = Modifier.size(14.dp))
                            }
                        } else {
                            Spacer(modifier = Modifier.width(24.dp))
                        }
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // ═══ BOTONES DE FILA ═══
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                OutlinedButton(
                    onClick = {
                        val nuevaFila = FilaVehiculo(List(columnasActuales) { 0 })
                        val nuevas = filasActuales.toMutableList()
                        if (filaSeleccionada in filasActuales.indices) {
                            nuevas.add(filaSeleccionada + 1, nuevaFila); filaSeleccionada = -1
                        } else nuevas.add(nuevaFila)
                        onFilasChange(nuevas)
                    },
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(14.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(if (filaSeleccionada >= 0) "Insertar fila debajo" else "Agregar fila", fontSize = 11.sp)
                }
                if (filaSeleccionada in filasActuales.indices) {
                    OutlinedButton(
                        onClick = {
                            val nuevas = filasActuales.toMutableList()
                            // La copia entra sin números ni volante: se numera tocándola.
                            nuevas.add(filaSeleccionada + 1, FilaVehiculo(filasActuales[filaSeleccionada].asientos.map { 0 }))
                            filaSeleccionada = -1
                            onFilasChange(nuevas)
                        },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(8.dp),
                        border = BorderStroke(1.dp, SmartVanColors.CyanAccent)
                    ) {
                        Icon(Icons.Filled.ContentCopy, contentDescription = null, modifier = Modifier.size(14.dp), tint = SmartVanColors.CyanAccent)
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Fila vacía igual", fontSize = 11.sp, color = SmartVanColors.CyanAccent)
                    }
                }
            }

            // ═══ RESUMEN ═══
            Card(
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
            ) {
                Row(modifier = Modifier.padding(10.dp), horizontalArrangement = Arrangement.SpaceEvenly, verticalAlignment = Alignment.CenterVertically) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text("$totalAsientos", fontWeight = FontWeight.Black, color = SmartVanColors.Verde, fontSize = 20.sp)
                        Text("Asientos", color = Color.Gray, fontSize = 10.sp)
                    }
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text("${filasActuales.size}", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro, fontSize = 20.sp)
                        Text("Filas", color = Color.Gray, fontSize = 10.sp)
                    }
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text("$columnasActuales", fontWeight = FontWeight.Black, color = SmartVanColors.CyanAccent, fontSize = 20.sp)
                        Text("Columnas", color = Color.Gray, fontSize = 10.sp)
                    }
                }
            }
        }
      }

      val vistaPrevia = @Composable {
        // Números que existen ahora mismo: al quitar un asiento la selección de prueba
        // no debe quedar apuntando a un asiento que ya no está.
        val numerosVigentes = filasActuales.flatMap { f -> f.asientos.filter { it > 0 } }.toSet()
        val elegidos = seleccionPreview intersect numerosVigentes

        Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
            Text("Así lo verá el cliente", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
            Text("Vista previa en vivo de la pantalla de selección de asientos", color = Color.Gray, fontSize = 10.sp)
            Spacer(modifier = Modifier.height(6.dp))
            Row(
                modifier = Modifier.fillMaxWidth()
                    .background(SmartVanColors.FondoCyan, RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 6.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                Icon(Icons.Filled.TouchApp, contentDescription = null, tint = Color(0xFF006064), modifier = Modifier.size(14.dp))
                Text(
                    if (elegidos.isEmpty()) "Toca un asiento para probarlo, igual que el cliente."
                    else "El cliente eligió: ${elegidos.sorted().joinToString(", ")}",
                    fontSize = 10.sp, color = Color(0xFF006064),
                    modifier = Modifier.weight(1f)
                )
                if (elegidos.isNotEmpty()) {
                    TextButton(
                        onClick = { seleccionPreview = emptySet() },
                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 0.dp)
                    ) { Text("Limpiar", fontSize = 10.sp, color = SmartVanColors.Azul) }
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            MapaAsientosVehiculo(
                vehiculo = VehiculoConfig(
                    nombre = "preview",
                    capacidad = totalAsientos,
                    columnas = columnasActuales,
                    filas = filasActuales
                ),
                ocupados = emptySet(),
                seleccionados = elegidos,
                onToggleAsiento = { n ->
                    seleccionPreview = if (n in seleccionPreview) seleccionPreview - n else seleccionPreview + n
                }
            )
        }
      }

      if (ladoALado) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            Box(modifier = Modifier.weight(1f)) { editor() }
            Box(modifier = Modifier.weight(1f)) { vistaPrevia() }
        }
      } else {
        Column(modifier = Modifier.fillMaxWidth()) {
            editor()
            Spacer(modifier = Modifier.height(16.dp))
            HorizontalDivider(color = SmartVanColors.Borde)
            Spacer(modifier = Modifier.height(12.dp))
            vistaPrevia()
        }
      }
    }
}
